import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import api from '../../lib/api';
import { errMsg, fmtMoney } from '../../lib/format';

const EMPTY_FORM = { descripcion: '', unidad: 'pza', indirect_percentage: 0, financing_percentage: 0, utility_percentage: 0 };
const EMPTY_ROW = {
  materials: { descripcion: '', unidad: 'pza', cantidad: 1, costo_unitario: 0, moneda: 'MXN', catalog_numero: null },
  labor: { categoria: '', salario_base: 0, factor_salario_real: 1.45, rendimiento: 1, moneda: 'MXN', catalog_numero: null },
  equipment: { tipo: '', descripcion: '', cantidad: 1, costo_unitario: 0, moneda: 'MXN', catalog_numero: null },
};

const Cell = ({ value, onChange, type = 'text', width = 'w-full', testid, step }) => (
  <Input
    type={type}
    step={step || (type === 'number' ? '0.01' : undefined)}
    value={value ?? ''}
    onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
    className={`h-8 ${width} ${type === 'number' ? 'text-right font-mono' : ''} text-[12.5px] px-2`}
    data-testid={testid}
  />
);

const MonedaSel = ({ value, onChange, testid }) => (
  <Select value={value || 'MXN'} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-[74px] text-[12px]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
  </Select>
);

/**
 * Editor completo de concepto de catálogo: datos generales + APU embebido (3 tabs).
 * itemId=null → crear nuevo concepto.
 */
export const ConceptCatalogDialog = ({ open, onOpenChange, itemId, scope, onSaved }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [rows, setRows] = useState({ materials: [], labor: [], equipment: [] });

  const { data: detail, isLoading } = useQuery({
    queryKey: ['catalog-concept-detail', itemId],
    queryFn: async () => (await api.get(`/catalog/concepts/${itemId}/detail`)).data,
    enabled: open && !!itemId,
  });

  const { data: appSettings } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => (await api.get('/settings')).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (itemId && detail) {
      setForm({
        descripcion: detail.descripcion || '',
        unidad: detail.unidad || 'pza',
        indirect_percentage: detail.indirect_percentage ?? 0,
        financing_percentage: detail.financing_percentage ?? 0,
        utility_percentage: detail.utility_percentage ?? 0,
      });
      setRows({
        materials: detail.materials || [],
        labor: detail.labor || [],
        equipment: detail.equipment || [],
      });
    } else if (!itemId) {
      // Prellenar porcentajes desde Configuración
      const d = (appSettings?.apply_defaults_to_new_concepts !== false && appSettings?.concept_defaults?.[scope]) || {};
      setForm({
        ...EMPTY_FORM,
        indirect_percentage: d.indirect_pct ?? 0,
        financing_percentage: d.financing_pct ?? 0,
        utility_percentage: d.utility_pct ?? 0,
      });
      setRows({ materials: [], labor: [], equipment: [] });
    }
  }, [open, itemId, detail, appSettings, scope]);

  const setRow = (type, idx, key, value) => {
    setRows((r) => ({ ...r, [type]: r[type].map((row, i) => (i === idx ? { ...row, [key]: value } : row)) }));
  };
  const addRow = (type) => setRows((r) => ({ ...r, [type]: [...r[type], { ...EMPTY_ROW[type] }] }));
  const removeRow = (type, idx) => setRows((r) => ({ ...r, [type]: r[type].filter((_, i) => i !== idx) }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { ...form, scope, ...rows };
      if (itemId) return (await api.put(`/catalog/concepts/${itemId}`, payload)).data;
      return (await api.post('/catalog/concepts', payload)).data;
    },
    onSuccess: (item) => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      queryClient.invalidateQueries({ queryKey: ['catalog-concept-detail', itemId] });
      toast.success(itemId ? `Concepto ${item.numero} actualizado` : `Concepto ${item.numero} registrado en catálogo`);
      onOpenChange(false);
      if (onSaved) onSaved(item);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const subtotal = {
    materials: rows.materials.reduce((a, m) => a + (Number(m.cantidad) || 0) * (Number(m.costo_unitario) || 0), 0),
    labor: rows.labor.reduce((a, l) => {
      const rend = Number(l.rendimiento) || 1;
      return a + ((Number(l.salario_base) || 0) * (Number(l.factor_salario_real) || 1)) / (rend || 1);
    }, 0),
    equipment: rows.equipment.reduce((a, e) => a + (Number(e.cantidad) || 0) * (Number(e.costo_unitario) || 0), 0),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="concept-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            {itemId ? `Editar concepto ${detail?.numero || ''}` : 'Nuevo concepto de catálogo'}
            <span className="ml-2 text-[12px] font-normal text-muted-foreground">({scope === 'pv' ? 'Fotovoltaico' : 'Prefab'})</span>
          </DialogTitle>
        </DialogHeader>

        {isLoading && itemId ? (
          <div className="py-10 text-center text-muted-foreground text-sm">Cargando detalle…</div>
        ) : (
          <>
            {/* Datos generales */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="col-span-2 md:col-span-3 space-y-1.5">
                <Label>Descripción *</Label>
                <Input value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} data-testid="concept-cat-descripcion" />
              </div>
              <div className="space-y-1.5">
                <Label>Unidad</Label>
                <Input value={form.unidad} onChange={(e) => setForm((f) => ({ ...f, unidad: e.target.value }))} data-testid="concept-cat-unidad" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px]">% Indirectos</Label>
                <Input type="number" step="0.01" value={form.indirect_percentage} onChange={(e) => setForm((f) => ({ ...f, indirect_percentage: Number(e.target.value) }))} data-testid="concept-cat-ind" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px]">% Utilidad</Label>
                <Input type="number" step="0.01" value={form.utility_percentage} onChange={(e) => setForm((f) => ({ ...f, utility_percentage: Number(e.target.value) }))} data-testid="concept-cat-uti" />
              </div>
            </div>

            <Tabs defaultValue="materials" className="mt-2">
              <TabsList>
                <TabsTrigger value="materials" data-testid="concept-cat-tab-materiales">Materiales ({rows.materials.length})</TabsTrigger>
                <TabsTrigger value="labor" data-testid="concept-cat-tab-mano-obra">Mano de Obra ({rows.labor.length})</TabsTrigger>
                <TabsTrigger value="equipment" data-testid="concept-cat-tab-equipo">Equipo ({rows.equipment.length})</TabsTrigger>
              </TabsList>

              {/* MATERIALES */}
              <TabsContent value="materials" className="space-y-3">
                <Table data-testid="concept-cat-materials-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="th-label">Catálogo</TableHead>
                      <TableHead className="th-label min-w-[200px]">Descripción</TableHead>
                      <TableHead className="th-label">Unidad</TableHead>
                      <TableHead className="th-label">Cantidad</TableHead>
                      <TableHead className="th-label text-right">Costo unit.</TableHead>
                      <TableHead className="th-label">Moneda</TableHead>
                      <TableHead className="th-label w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.materials.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-5">Sin materiales. Agrega líneas al APU del concepto.</TableCell></TableRow>
                    )}
                    {rows.materials.map((m, i) => (
                      <TableRow key={i} className="hover:bg-background/40">
                        <TableCell className="font-mono text-[12px] text-muted-foreground">{m.catalog_numero || '—'}</TableCell>
                        <TableCell><Cell value={m.descripcion} onChange={(v) => setRow('materials', i, 'descripcion', v)} testid={`cat-mat-desc-${i}`} /></TableCell>
                        <TableCell><Cell value={m.unidad} onChange={(v) => setRow('materials', i, 'unidad', v)} width="w-20" testid={`cat-mat-unidad-${i}`} /></TableCell>
                        <TableCell><Cell value={m.cantidad} type="number" step="0.0001" onChange={(v) => setRow('materials', i, 'cantidad', v)} width="w-24" testid={`cat-mat-cantidad-${i}`} /></TableCell>
                        <TableCell><Cell value={m.costo_unitario} type="number" onChange={(v) => setRow('materials', i, 'costo_unitario', v)} width="w-24" testid={`cat-mat-costo-${i}`} /></TableCell>
                        <TableCell><MonedaSel value={m.moneda} onChange={(v) => setRow('materials', i, 'moneda', v)} testid={`cat-mat-moneda-${i}`} /></TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeRow('materials', i)} data-testid={`cat-mat-delete-${i}`}>
                            <Trash2 size={13} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" className="h-8" onClick={() => addRow('materials')} data-testid="cat-add-material-row">
                    <Plus size={14} className="mr-1.5" /> Agregar material
                  </Button>
                  <div className="text-[13px]"><span className="text-muted-foreground mr-2">Subtotal (estimado):</span><span className="money font-medium">{fmtMoney(subtotal.materials)}</span></div>
                </div>
              </TabsContent>

              {/* MANO DE OBRA */}
              <TabsContent value="labor" className="space-y-3">
                <Table data-testid="concept-cat-labor-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="th-label">Catálogo</TableHead>
                      <TableHead className="th-label min-w-[160px]">Categoría</TableHead>
                      <TableHead className="th-label text-right">Salario base</TableHead>
                      <TableHead className="th-label">Factor</TableHead>
                      <TableHead className="th-label">Rendimiento</TableHead>
                      <TableHead className="th-label">Moneda</TableHead>
                      <TableHead className="th-label w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.labor.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-5">Sin mano de obra</TableCell></TableRow>
                    )}
                    {rows.labor.map((l, i) => (
                      <TableRow key={i} className="hover:bg-background/40">
                        <TableCell className="font-mono text-[12px] text-muted-foreground">{l.catalog_numero || '—'}</TableCell>
                        <TableCell><Cell value={l.categoria} onChange={(v) => setRow('labor', i, 'categoria', v)} testid={`cat-mo-categoria-${i}`} /></TableCell>
                        <TableCell><Cell value={l.salario_base} type="number" onChange={(v) => setRow('labor', i, 'salario_base', v)} width="w-24" testid={`cat-mo-salario-${i}`} /></TableCell>
                        <TableCell><Cell value={l.factor_salario_real} type="number" step="0.0001" onChange={(v) => setRow('labor', i, 'factor_salario_real', v)} width="w-20" testid={`cat-mo-factor-${i}`} /></TableCell>
                        <TableCell><Cell value={l.rendimiento} type="number" step="0.0001" onChange={(v) => setRow('labor', i, 'rendimiento', v)} width="w-24" testid={`cat-mo-rendimiento-${i}`} /></TableCell>
                        <TableCell><MonedaSel value={l.moneda} onChange={(v) => setRow('labor', i, 'moneda', v)} testid={`cat-mo-moneda-${i}`} /></TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeRow('labor', i)} data-testid={`cat-mo-delete-${i}`}>
                            <Trash2 size={13} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" className="h-8" onClick={() => addRow('labor')} data-testid="cat-add-labor-row">
                    <Plus size={14} className="mr-1.5" /> Agregar mano de obra
                  </Button>
                  <div className="text-[13px]"><span className="text-muted-foreground mr-2">Subtotal (estimado):</span><span className="money font-medium">{fmtMoney(subtotal.labor)}</span></div>
                </div>
              </TabsContent>

              {/* EQUIPO */}
              <TabsContent value="equipment" className="space-y-3">
                <Table data-testid="concept-cat-equipment-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="th-label">Catálogo</TableHead>
                      <TableHead className="th-label min-w-[180px]">Descripción</TableHead>
                      <TableHead className="th-label">Tipo</TableHead>
                      <TableHead className="th-label">Cantidad</TableHead>
                      <TableHead className="th-label text-right">Costo unit.</TableHead>
                      <TableHead className="th-label">Moneda</TableHead>
                      <TableHead className="th-label w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.equipment.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-5">Sin equipo</TableCell></TableRow>
                    )}
                    {rows.equipment.map((e, i) => (
                      <TableRow key={i} className="hover:bg-background/40">
                        <TableCell className="font-mono text-[12px] text-muted-foreground">{e.catalog_numero || '—'}</TableCell>
                        <TableCell><Cell value={e.descripcion} onChange={(v) => setRow('equipment', i, 'descripcion', v)} testid={`cat-eq-desc-${i}`} /></TableCell>
                        <TableCell><Cell value={e.tipo} onChange={(v) => setRow('equipment', i, 'tipo', v)} width="w-24" testid={`cat-eq-tipo-${i}`} /></TableCell>
                        <TableCell><Cell value={e.cantidad} type="number" step="0.0001" onChange={(v) => setRow('equipment', i, 'cantidad', v)} width="w-24" testid={`cat-eq-cantidad-${i}`} /></TableCell>
                        <TableCell><Cell value={e.costo_unitario} type="number" onChange={(v) => setRow('equipment', i, 'costo_unitario', v)} width="w-24" testid={`cat-eq-costo-${i}`} /></TableCell>
                        <TableCell><MonedaSel value={e.moneda} onChange={(v) => setRow('equipment', i, 'moneda', v)} testid={`cat-eq-moneda-${i}`} /></TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeRow('equipment', i)} data-testid={`cat-eq-delete-${i}`}>
                            <Trash2 size={13} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" className="h-8" onClick={() => addRow('equipment')} data-testid="cat-add-equipment-row">
                    <Plus size={14} className="mr-1.5" /> Agregar equipo
                  </Button>
                  <div className="text-[13px]"><span className="text-muted-foreground mr-2">Subtotal (estimado):</span><span className="money font-medium">{fmtMoney(subtotal.equipment)}</span></div>
                </div>
              </TabsContent>
            </Tabs>

            <Separator />
            <p className="text-[12px] text-muted-foreground">
              Los subtotales son estimados en la moneda de captura. Al usar este concepto en una cotización, el precio se calcula con el motor APU (tipo de cambio, cascada e IVA).
            </p>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => saveMutation.mutate()} disabled={!form.descripcion || saveMutation.isPending} data-testid="concept-cat-save-button">
            {itemId ? 'Guardar cambios' : 'Registrar concepto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
